import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Dropdown, TextInput, Image } from "hesus-ui-kit";
import ServiceHeader from "./ServiceHeader";
import InvoiceTotal from "./InvoiceTotal";
import deleteIcon from "@icons/ic-delete-red.svg";
import todoIcon from "@icons/ic-todo.svg";
import PulseLoader from "react-spinners/PulseLoader";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetMultiUserCurrenciesByCountryId } from "@hooks/useGeneralParams";
import {
  renderPriceWithCurrency,
  renderPriceWithCurrencyWithoutUnit,
  updateUnitTranslation,
  addSpace,
} from "@utils/utils";
import { labelStyles, inputStyles, inputStylesError } from "@utils/extra-style";
import {
  GridRow,
  GridCell,
  ErrorMessagePara,
  InvoiceButton,
  QuantityLineOuter,
  ContainerLabel,
} from "../../AddEditInvoice.styled";
import "../../AddEditInvoice.scss";
import RealisedQuantityPopup from "../RealisedQuantityPopup";

const InvoiceServices = ({
  t,
  serviceItems,
  extraItems,
  currencyId,
  currencyPosition,
  countryId,
  exchangeCurrencies,
  exchangeRates,
  handleExchangeRateChange,
  handleQuantityChange,
  vatRateList,
  vatRateId,
  onAddExtraItem,
  onDeleteExtraItem,
  handleExtraItemChange,
  handleFreeTextClick,
  hesusCatalogList,
  unitList,
  serviceLoading,
  serviceSectionError,
  extraItemsErrors,
  realisedQuantityLoading,
  setServiceItems,
  startDate,
  endDate,
  handleRealisedQuantityPopupOpen,
  documentType,
  serviceItemsErrors,
  exchangeRateErrors,
}) => {
  const userCurrencyObject = useGetMultiUserCurrenciesByCountryId(countryId);

  const [openRealisedQuantityId, setOpenRealisedQuantityId] = useState(null);
  const parseDecimal = (value) => {
    if (value === null || value === undefined || value === "") {
      return NaN;
    }
    return Number(String(value).replace(",", "."));
  };

  const hasMissingExchangeRate = () => {
    const invoiceCurrencyId = Number(currencyId);

    return (serviceItems ?? []).some((service) => {
      const quoteCurrencyId = Number(service.quoteService?.currency?.id);

      // No exchange rate is required for the same currency
      if (quoteCurrencyId === invoiceCurrencyId) {
        return false;
      }

      // Check whether a valid exchange rate exists
      const exchangeRate = exchangeRates.find(
        (rate) =>
          Number(rate.currencyFrom) === quoteCurrencyId &&
          Number(rate.currencyTo) === invoiceCurrencyId
      );

      const value = Number(
        String(exchangeRate?.exchangeRate ?? "").replace(",", ".")
      );

      return (
        !exchangeRate ||
        exchangeRate.exchangeRate === "" ||
        exchangeRate.exchangeRate === null ||
        !Number.isFinite(value) ||
        value <= 0
      );
    });
  };

  const getItemTotal = (item, service) => {
    if (item.quantity === null || item.quantity === "") {
      return "-";
    }
    const quantity = parseDecimal(item.quantity);
    const sellingPrice = parseDecimal(item.sellingPrice);
    if (Number.isNaN(quantity)) {
      return "-";
    }
    if (item.sellingPrice === null || item.sellingPrice === "") {
      return "-";
    }
    if (Number.isNaN(sellingPrice)) {
      return "-";
    }
    let total = sellingPrice * quantity;

    const quoteCurrencyId = Number(service.quoteService?.currency?.id);
    const invoiceCurrencyId = Number(currencyId);

    if (quoteCurrencyId !== invoiceCurrencyId) {
      const exchangeRate = exchangeRates.find(
        (rate) =>
          Number(rate.currencyFrom) === quoteCurrencyId &&
          Number(rate.currencyTo) === invoiceCurrencyId
      );

      const rate = parseDecimal(exchangeRate?.exchangeRate);

      if (!rate || Number.isNaN(rate)) {
        return "-";
      }

      total *= rate;
    }

    return total.toFixed(2);
  };

  const getExtraItemTotal = (item) => {
    const quantity = parseDecimal(item.quantity);
    const sellingPrice = parseDecimal(item.sellingPrice);

    if (
      item.quantity === "" ||
      item.quantity === null ||
      item.sellingPrice === "" ||
      item.sellingPrice === null ||
      Number.isNaN(quantity) ||
      Number.isNaN(sellingPrice)
    ) {
      return "-";
    }

    return (quantity * sellingPrice).toFixed(2);
  };

  const getGrandTotalWithoutTaxes = () => {
    if (hasMissingExchangeRate()) return "-";
    let grandTotal = 0;
    let hasValidTotal = false;

    // Existing quote items
    (serviceItems ?? []).forEach((service) => {
      (service.quoteItems ?? []).forEach((item) => {
        const total = getItemTotal(item, service);

        if (total !== "-") {
          grandTotal += Number(total);
          hasValidTotal = true;
        }
      });
    });

    // Optional additional items
    (extraItems ?? []).forEach((item) => {
      const total = getExtraItemTotal(item);

      if (total !== "-") {
        grandTotal += Number(total);
        hasValidTotal = true;
      }
    });

    return hasValidTotal ? grandTotal.toFixed(2) : "-";
  };

  const getSelectedVatDetail = () => {
    const vatRate = vatRateList.find(
      (rate) => Number(rate.value) === Number(vatRateId)
    );

    return vatRate?.label || "";
  };

  const getTaxesAmount = () => {
    if (hasMissingExchangeRate()) return "-";
    const grandTotal = getGrandTotalWithoutTaxes();

    if (grandTotal === "-") {
      return "-";
    }
    const vatRate = vatRateList.find(
      (rate) => Number(rate.value) === Number(vatRateId)
    );
    const rate = Number(vatRate?.calculationRate) || 0;
    return Number(grandTotal * rate).toFixed(2);
  };

  const getTotalIncludingTaxes = () => {
    if (hasMissingExchangeRate()) return "-";
    const grandTotal = getGrandTotalWithoutTaxes();
    if (grandTotal === "-") {
      return "-";
    }
    const taxes = getTaxesAmount();
    return (Number(grandTotal) + Number(taxes)).toFixed(2);
  };

  const getExchangeRateInfo = (item, service) => {
    if (
      item.quantity === null ||
      item.quantity === undefined ||
      item.quantity === "" ||
      Number(item.quantity) === 0
    ) {
      return null;
    }
    const invoiceCurrencyId = Number(currencyId);
    const quoteCurrencyId = Number(service.quoteService?.currency?.id);

    // Same currency → no conversion needed
    if (quoteCurrencyId === invoiceCurrencyId) {
      return null;
    }

    const exchange = exchangeRates.find(
      (rate) =>
        Number(rate.currencyFrom) === quoteCurrencyId &&
        Number(rate.currencyTo) === invoiceCurrencyId
    );

    const rate = parseDecimal(exchange?.exchangeRate);

    // Different currency but exchange rate is missing
    if (
      !exchange ||
      exchange.exchangeRate === "" ||
      exchange.exchangeRate === null ||
      exchange.exchangeRate === undefined ||
      Number.isNaN(rate)
    ) {
      return (
        <div
          style={{
            color: "#E83F1E",
            marginLeft: "8px",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          {t("CLIENT_INVOICING_SERVICES_ER_MISSING")}
        </div>
      );
    }

    const convertedSellingPrice = parseDecimal(item.sellingPrice) * rate;

    return (
      <div
        style={{
          color: "#FF981E",
          marginLeft: "8px",
          fontStyle: "italic",
          fontSize: "15px",
        }}
      >
        {renderPriceWithCurrency(
          convertedSellingPrice.toFixed(2),
          t(item.sellingUnit),
          userCurrencyObject[currencyId],
          currencyPosition
        )}
      </div>
    );
  };

  const handleSummarySelection = ({ summaries, quantityRealised }) => {
    setServiceItems((prevServiceItems) =>
      prevServiceItems.map((serviceItem) => ({
        ...serviceItem,
        quoteItems: serviceItem.quoteItems?.map((quoteItem) =>
          quoteItem.quoteItemId === openRealisedQuantityId
            ? {
                ...quoteItem,
                quantityRealised,
                realisedQuantityData: {
                  ...quoteItem.realisedQuantityData,
                  totalSelected: quantityRealised,
                  summaries,
                },
              }
            : quoteItem
        ),
      }))
    );
  };

  const serviceItemColumns =
    "minmax(0, 2fr) minmax(180px, 0.8fr) minmax(180px, 0.8fr) minmax(150px, 0.7fr)";

  return (
    <>
      <GridRow
        background="#008395"
        borderRadius="5px 5px 0 0"
        column="1fr auto"
      >
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_SERVICES")}
        </GridCell>

        <InvoiceButton
          type="button"
          style={{
            background: "#fff",
            color: "#43869f",
            border: "1px solid #ddd",
            borderRadius: "4px",
            height: "25px",
            minWidth: "145px",
            fontSize: "16px",
            fontWeight: "700",
            marginTop: "0px",
            display: "block",
          }}
          className="add-edit-invoice__add-extra-item"
          onClick={onAddExtraItem}
        >
          {t("CLIENT_INVOICING_ADD_SERVICE_ACTION")}
        </InvoiceButton>
      </GridRow>
      <GridRow
        padding="0"
        column="1fr"
        boxShadow="0px 1px 1px 1px #e5e5e5"
        borderRadius="0 0 5px 5px"
        gap="0"
      >
        {serviceSectionError.length > 0 && (
          <GridRow gap="10px" padding="12px 16px 0px">
            {serviceSectionError.map((error, index) => (
              <ErrorMessagePara key={index}>{error}</ErrorMessagePara>
            ))}
          </GridRow>
        )}
        {serviceItems.length === 0 &&
          extraItems.length === 0 &&
          !serviceLoading && (
            <GridRow padding="10px 12px">
              <GridCell
                color="#777"
                fontSize="16px"
                fontWeight="400"
                fontStyle="italic"
              >
                {t("CLIENT_INVOICING_NO_SERVICES")}
              </GridCell>
            </GridRow>
          )}
        {serviceLoading && (
          <GridRow padding="10px 12px">
            <GridCell
              color="#777"
              fontSize="16px"
              fontWeight="400"
              fontStyle="italic"
            >
              <div className="suggestions-list-loading">
                <PulseLoader color="#1bace4" size={15} />
              </div>
            </GridCell>
          </GridRow>
        )}
        {!serviceLoading && <ServiceHeader column={serviceItemColumns} />}

        <GridRow
          column="1fr"
          padding={
            extraItems.length > 0 ? "0 10px 0px 10px" : "0 10px 10px 10px"
          }
          gap="0px"
        >
          {serviceItems.map((service, serviceIndex) => (
            <GridRow
              key={
                "invoice-service-" +
                serviceIndex +
                "-" +
                service.quoteService.id
              }
              column="1fr"
              padding="0"
              gap="0px"
              border="1px solid #b7c9df"
              borderTop={serviceIndex === 0 ? "1px solid #b7c9df" : "none"}
              borderRadius={serviceIndex === 0 ? "4px 4px 0 0" : "0"}
              overflow="hidden"
            >
              {/* Quote Service Header */}
              <GridRow
                column="1fr"
                padding="7px 10px"
                background="#d8e4f7"
                borderBottom="1px solid #b7c9df"
                gap="0"
              >
                <GridCell color="#29458a" fontSize="16px" fontWeight="700">
                  {service.quoteService.label}
                </GridCell>
              </GridRow>

              {/* Quote Items */}
              {service.quoteItems.map((item, index) => {
                return (
                  <GridRow
                    key={"quoteItems" + index + "-" + item.quoteItem}
                    column={serviceItemColumns}
                    padding="0"
                    background="#fff"
                    gap="0"
                    borderBottom={
                      index === service.quoteItems.length - 1
                        ? "1px solid #b7c9df"
                        : "1px solid #d5dce5"
                    }
                  >
                    {/* Item + Price */}
                    <GridRow column="1fr auto" padding="7px 12px" gap="15px">
                      <GridCell color="#333" fontSize="16px" fontWeight="700">
                        <div>{item.quoteItemLabel}</div>

                        {!!item.hesusService.needProduct && (
                          <div
                            style={{
                              color: "#767676",
                              fontSize: "14px",
                              fontStyle: "italic",
                              fontWeight: "400",
                              marginTop: "3px",
                            }}
                          >
                            {item.product.name}
                          </div>
                        )}
                        {!!item.hesusService.needTransport && (
                          <div
                            style={{
                              color: "#767676",
                              fontSize: "14px",
                              fontStyle: "italic",
                              fontWeight: "400",
                              marginTop: "3px",
                            }}
                          >
                            {!!item.hesusService.needProduct && " - "}
                            {item.transport.name}
                          </div>
                        )}
                      </GridCell>

                      <GridCell
                        color="#555"
                        fontSize="16px"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <div style={{ textAlign: "right" }}>
                          <div>
                            {renderPriceWithCurrency(
                              Number(item.sellingPrice).toFixed(2),
                              t(item.sellingUnit),
                              service.quoteService.currency.symbol,
                              service.quoteService.currency.position
                            )}
                          </div>
                          {getExchangeRateInfo(item, service)}
                        </div>
                      </GridCell>
                    </GridRow>

                    {/* Realised */}
                    <GridCell
                      color="#555"
                      fontSize="16px"
                      fontWeight="400"
                      display="grid"
                      alignItems="center"
                      style={{
                        borderLeft: "1px solid #b7c9df",
                        gridTemplateColumns: "1fr auto 1fr",
                      }}
                    >
                      <div />
                      {documentType !== 1 && "-"}
                      {documentType === 1 && (
                        <>
                          <div>
                            {item.quantityRealised}
                            {" " + t(item.unitRealised)}
                          </div>
                          <div
                            style={{
                              justifySelf: "end",
                              paddingRight: "10px",
                              cursor: "pointer",
                            }}
                          >
                            {realisedQuantityLoading[item.quoteItemId] ? (
                              <ClipLoader size={16} color="#1bace4" />
                            ) : (
                              <>
                                <Image
                                  src={todoIcon}
                                  alt={t(
                                    "CLIENT_INVOICING_SERVICES_SELECT_REALISED_TOOLTIP"
                                  )}
                                  title={t(
                                    "CLIENT_INVOICING_SERVICES_SELECT_REALISED_TOOLTIP"
                                  )}
                                  className="add-edit-invoice__image-view-icon"
                                  onClick={() => {
                                    const isOpening =
                                      openRealisedQuantityId !==
                                      item.quoteItemId;
                                    if (isOpening) {
                                      handleRealisedQuantityPopupOpen(
                                        service.quoteService,
                                        item
                                      );
                                    }
                                    setOpenRealisedQuantityId(
                                      isOpening ? item.quoteItemId : null
                                    );
                                  }}
                                />
                                {openRealisedQuantityId ===
                                  item.quoteItemId && (
                                  <RealisedQuantityPopup
                                    quoteItem={item}
                                    quoteService={service.quoteService}
                                    onClose={() =>
                                      setOpenRealisedQuantityId(null)
                                    }
                                    onSummarySelection={handleSummarySelection}
                                    startDate={startDate}
                                    endDate={endDate}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </GridCell>

                    {/* Quantity */}
                    <GridCell
                      color="#555"
                      fontSize="16px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      padding="5px"
                      style={{
                        borderLeft: "1px solid #b7c9df",
                        gap: "8px",
                      }}
                    >
                      <QuantityLineOuter>
                        <TextInput
                          type="text"
                          value={item.quantity ?? ""}
                          onChange={(e) =>
                            handleQuantityChange(
                              service.quoteService.id,
                              item.quoteItemId,
                              e.target.value
                            )
                          }
                          name={`invoice-qty-${item.quoteItemId}`}
                          id={`invoice-qty-${item.quoteItemId}`}
                          labelStyles={labelStyles}
                          // inputStyles={inputStyles}
                          inputStyles={
                            serviceItemsErrors?.[service.quoteService.id]?.[
                              item.quoteItemId
                            ]
                              ? inputStylesError
                              : inputStyles
                          }
                          containerStyles={{ maxWidth: "90px" }}
                        />{" "}
                        <span>{t(item.invoicedUnit)}</span>
                      </QuantityLineOuter>
                    </GridCell>

                    {/* Total */}
                    <GridCell
                      color="#444"
                      fontSize="16px"
                      fontWeight="700"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      style={{
                        borderLeft: "1px solid #b7c9df",
                        paddingRight: "14px",
                      }}
                    >
                      {renderPriceWithCurrencyWithoutUnit(
                        addSpace(getItemTotal(item, service)),
                        userCurrencyObject[currencyId],
                        currencyPosition
                      )}
                    </GridCell>
                  </GridRow>
                );
              })}
            </GridRow>
          ))}
        </GridRow>
      </GridRow>
      {extraItems.length > 0 && (
        <GridRow column="1fr" padding="0 10px 10px 10px" gap="0">
          {/* Additional services header */}
          <GridRow
            column="1fr"
            padding="7px 10px"
            background="#d8e4f7"
            border="1px solid #b7c9df"
            borderBottom="1px solid #b7c9df"
            gap="0"
          >
            <GridCell color="#29458a" fontSize="16px" fontWeight="700">
              {t("CLIENT_INVOICING_ADDITIONAL_SERVICES")}
            </GridCell>
          </GridRow>

          {extraItems.map((item, index) => {
            const errors = extraItemsErrors[index] || {};
            return (
              <GridRow
                key={`extra-item-${index}`}
                column={serviceItemColumns}
                padding="0"
                background="#fff"
                gap="0"
                border="1px solid #b7c9df"
                borderBottom={
                  index === extraItems.length - 1
                    ? "1px solid #b7c9df"
                    : "1px solid #d5dce5"
                }
              >
                {/* Service + Price + Unit + comment */}
                <div>
                  <GridRow column="1fr auto" padding="10px 12px" gap="15px">
                    <GridCell color="#333" fontSize="16px" fontWeight="700">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Image
                          className="deleteImage"
                          src={deleteIcon}
                          alt="delete"
                          onClick={() => onDeleteExtraItem(index)}
                          styles={{
                            width: "25px",
                            height: "25px",
                          }}
                        />

                        {/* Service label */}
                        {item.isFreeText ? (
                          <TextInput
                            type="text"
                            value={item.serviceLabel ?? ""}
                            onChange={(e) =>
                              handleExtraItemChange(
                                index,
                                "serviceLabel",
                                e.target.value
                              )
                            }
                            name={`extra-service-${index}`}
                            id={`extra-service-${index}`}
                            labelStyles={labelStyles}
                            containerStyles={{
                              minWidth: "150px",
                              maxWidth: "450px",
                              width: "100%",
                            }}
                            inputStyles={
                              errors.serviceLabel
                                ? inputStylesError
                                : inputStyles
                            }
                          />
                        ) : (
                          <>
                            <Dropdown
                              value={item.hesusCatalogId ?? ""}
                              onChange={(option) =>
                                handleExtraItemChange(
                                  index,
                                  "hesusCatalogId",
                                  option.value
                                )
                              }
                              options={hesusCatalogList}
                              containerStyles={{
                                minWidth: "150px",
                                maxWidth: "450px",
                                width: "100%",
                              }}
                              // inputStyles={{ border: "1px solid #474747" }}
                              inputStyles={
                                errors.hesusCatalogId
                                  ? inputStylesError
                                  : { border: "1px solid #474747" }
                              }
                              labelStyles={{ display: "none" }}
                            />

                            <ContainerLabel
                              color="#767676"
                              textDecoration="underline"
                              cursor="pointer"
                              fontStyle="italic"
                              onClick={() => handleFreeTextClick(index)}
                            >
                              {t("CLIENT_INVOICING_FREE_TEXT_SERVICE")}
                            </ContainerLabel>
                          </>
                        )}
                      </div>
                    </GridCell>

                    {/* Price + Unit */}
                    <GridCell
                      color="#555"
                      fontSize="16px"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        {currencyPosition !== 1 && (
                          <span>{userCurrencyObject[currencyId]}</span>
                        )}
                        <TextInput
                          type="text"
                          value={item.sellingPrice ?? ""}
                          onChange={(e) =>
                            handleExtraItemChange(
                              index,
                              "sellingPrice",
                              e.target.value
                            )
                          }
                          name={`extra-price-${index}`}
                          id={`extra-price-${index}`}
                          labelStyles={labelStyles}
                          inputStyles={
                            errors.sellingPrice ? inputStylesError : inputStyles
                          }
                        />
                        {currencyPosition === 1 && (
                          <span>{userCurrencyObject[currencyId]}</span>
                        )}
                        <span>/</span>
                        <Dropdown
                          value={item.sellingUnit ?? ""}
                          onChange={(option) =>
                            handleExtraItemChange(
                              index,
                              "sellingUnit",
                              option.value
                            )
                          }
                          options={updateUnitTranslation(unitList, t)}
                          containerStyles={{ minWidth: "110px" }}
                          // inputStyles={{ border: "1px solid #474747" }}
                          inputStyles={
                            errors.sellingUnit ? inputStylesError : inputStyles
                          }
                          labelStyles={{ display: "none" }}
                        />
                      </div>
                    </GridCell>
                  </GridRow>
                  <GridRow column="30px 1fr" gap="0px">
                    {/* clientComment */}
                    <div></div>
                    {item.showClientComment ? (
                      <TextInput
                        type="textarea"
                        value={item.clientComment ?? ""}
                        onChange={(e) =>
                          handleExtraItemChange(
                            index,
                            "clientComment",
                            e.target.value
                          )
                        }
                        name={`extra-clientComment-${index}`}
                        id={`extra-clientComment-${index}`}
                        labelStyles={labelStyles}
                        inputStyles={inputStyles}
                        containerStyles={{
                          marginTop: "8px",
                        }}
                      />
                    ) : (
                      <ContainerLabel
                        onClick={() =>
                          handleExtraItemChange(
                            index,
                            "showClientComment",
                            true
                          )
                        }
                        color="#0A4986"
                        textDecoration="underline"
                        cursor="pointer"
                        fontStyle="italic"
                      >
                        {t("CLIENT_INVOICING_ADD_DESCRIPTION")}
                      </ContainerLabel>
                    )}
                  </GridRow>
                </div>
                {/* Realised */}
                <GridCell
                  color="#555"
                  fontSize="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    borderLeft: "1px solid #b7c9df",
                    gap: "12px",
                  }}
                >
                  -
                </GridCell>

                {/* Quantity */}
                <GridCell
                  color="#555"
                  fontSize="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    borderLeft: "1px solid #b7c9df",
                    gap: "8px",
                  }}
                >
                  <QuantityLineOuter>
                    <TextInput
                      type="text"
                      value={item.quantity ?? ""}
                      onChange={(e) =>
                        handleExtraItemChange(index, "quantity", e.target.value)
                      }
                      name={`extra-qty-${index}`}
                      id={`extra-qty-${index}`}
                      labelStyles={labelStyles}
                      inputStyles={
                        errors.quantity ? inputStylesError : inputStyles
                      }
                      containerStyles={{ minWidth: "90px" }}
                    />

                    <span>{t(item.sellingUnit)}</span>
                  </QuantityLineOuter>
                </GridCell>

                {/* Total */}
                <GridCell
                  color="#444"
                  fontSize="16px"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  style={{
                    borderLeft: "1px solid #b7c9df",
                    paddingRight: "14px",
                  }}
                >
                  {renderPriceWithCurrencyWithoutUnit(
                    addSpace(getExtraItemTotal(item)),
                    userCurrencyObject[currencyId],
                    currencyPosition
                  )}
                </GridCell>
              </GridRow>
            );
          })}
        </GridRow>
      )}
      <InvoiceTotal
        exchangeCurrencies={exchangeCurrencies}
        invoiceCurrency={userCurrencyObject[currencyId]}
        exchangeRates={exchangeRates}
        handleExchangeRateChange={handleExchangeRateChange}
        totalExculdingTxes={renderPriceWithCurrencyWithoutUnit(
          addSpace(getGrandTotalWithoutTaxes()),
          userCurrencyObject[currencyId],
          currencyPosition
        )}
        taxesAmount={renderPriceWithCurrencyWithoutUnit(
          addSpace(getTaxesAmount()),
          userCurrencyObject[currencyId],
          currencyPosition
        )}
        totalInculdingTxes={renderPriceWithCurrencyWithoutUnit(
          addSpace(getTotalIncludingTaxes()),
          userCurrencyObject[currencyId],
          currencyPosition
        )}
        selectedVatDetail={getSelectedVatDetail()}
        currencyId={currencyId}
        exchangeRateErrors={exchangeRateErrors}
      />
    </>
  );
};

InvoiceServices.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  serviceLoading: PropTypes.bool,
  updateRefreshStatus: PropTypes.func,
};

InvoiceServices.defaultProps = {
  isEditMode: false,
  id: null,
  updateRefreshStatus: null,
  serviceLoading: false,
};

export default withTranslation()(InvoiceServices);
